import React, { useEffect, useRef, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Loader2, Upload, Trash2, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "company_name", label: "Company / brand name", placeholder: "Sashikunja Educational Services LLP", hint: "Printed top-right of the report" },
  { key: "tagline", label: "Tagline", placeholder: "by Grow Up Once Again" },
  { key: "address", label: "Registered address", placeholder: "Street, City, State, PIN", multiline: true },
  { key: "gst_number", label: "GST number", placeholder: "22AAAAA0000A1Z5" },
  { key: "cin_number", label: "CIN / LLPIN", placeholder: "AAB-1234" },
  { key: "contact_email", label: "Contact email", placeholder: "hello@example.com" },
  { key: "contact_phone", label: "Contact phone", placeholder: "+91 00000 00000" },
  { key: "website", label: "Website", placeholder: "www.example.com" },
  { key: "footer_note", label: "Footer note", placeholder: "Any disclaimer, legal line or credit", multiline: true },
];

const AdminBranding = ({ onAuthError }) => {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stamp, setStamp] = useState(Date.now());
  const fileRef = useRef(null);
  const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/branding/logo?v=${stamp}`;

  const load = () => adminApi.get("/admin/branding").then((r) => setB(r.data.branding)).catch(onAuthError);
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { logo_path, logo_content_type, logo_updated_at, ...payload } = b;
      const { data } = await adminApi.put("/admin/branding", payload);
      setB(data.branding);
      toast.success("Report branding saved");
    } catch (e) { onAuthError?.(e); toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await adminApi.post("/admin/branding/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setB(data.branding);
      setStamp(Date.now());
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Logo upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try {
      const { data } = await adminApi.delete("/admin/branding/logo");
      setB(data.branding);
      toast.success("Logo removed");
    } catch (e) { toast.error("Could not remove the logo"); }
  };

  if (!b) return <div className="flex justify-center py-20 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="max-w-3xl space-y-4" data-testid="admin-branding">
      <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
        <p className="text-sm text-slate-500">Everything here is optional. Switch on only what you want printed on member reports (2nd Income Blueprint PDF). Member resumes and cover letters stay unbranded.</p>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-slate-800">Report logo</p>
            <p className="text-xs text-slate-500 mt-0.5">PNG, JPG, WEBP or SVG up to 2 MB. Prints at the top-left of the report.</p>
          </div>
          <Switch checked={!!b.show_logo} onCheckedChange={(v) => setB({ ...b, show_logo: v })} data-testid="branding-show-logo" />
        </div>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="h-20 w-48 shrink-0 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden" data-testid="branding-logo-preview">
            {b.logo_path ? <img src={logoUrl} alt="Report logo" className="max-h-16 max-w-[11rem] object-contain" />
              : <span className="text-xs text-slate-400 flex items-center gap-1.5"><ImageIcon className="w-4 h-4" /> No logo</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="branding-logo-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10 bg-sky-600 hover:bg-sky-500 text-white" data-testid="branding-logo-upload">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> {b.logo_path ? "Replace logo" : "Upload logo"}</>}
            </Button>
            {b.logo_path && <Button onClick={removeLogo} variant="outline" className="h-10 border-slate-300 text-rose-600 hover:bg-rose-50" data-testid="branding-logo-remove"><Trash2 className="w-4 h-4 mr-2" /> Remove</Button>}
          </div>
          <div className="sm:ml-auto">
            <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Logo width (mm)</label>
            <Input type="number" min={10} max={70} value={b.logo_width_mm ?? 32} onChange={(e) => setB({ ...b, logo_width_mm: Number(e.target.value) })} className="w-24 bg-slate-50 border-slate-300 text-slate-900" data-testid="branding-logo-width" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 space-y-5">
        {FIELDS.map((f) => (
          <div key={f.key} data-testid={`branding-field-${f.key}`}>
            <div className="flex items-center justify-between gap-4 mb-1.5">
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500">{f.label}</label>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">{b[`show_${f.key}`] ? "On report" : "Hidden"}</span>
                <Switch checked={!!b[`show_${f.key}`]} onCheckedChange={(v) => setB({ ...b, [`show_${f.key}`]: v })} data-testid={`branding-show-${f.key}`} />
              </div>
            </div>
            {f.multiline
              ? <Textarea value={b[f.key] || ""} onChange={(e) => setB({ ...b, [f.key]: e.target.value })} placeholder={f.placeholder} className="bg-slate-50 border-slate-300 text-slate-900" data-testid={`branding-input-${f.key}`} />
              : <Input value={b[f.key] || ""} onChange={(e) => setB({ ...b, [f.key]: e.target.value })} placeholder={f.placeholder} className="bg-slate-50 border-slate-300 text-slate-900" data-testid={`branding-input-${f.key}`} />}
            {f.hint && <p className="text-[11px] text-slate-400 mt-1">{f.hint}</p>}
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-500 text-white" data-testid="admin-branding-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save branding"}
      </Button>
    </div>
  );
};

export default AdminBranding;
